import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError, AxiosRequestConfig } from "axios";

import ClientBase from "@citrine/client";

import { BranchRootClient } from "../branch-root";
import { transformConstraint } from "../constraint";
import type { ExecutionCandidate } from "../execution-candidate";
import type { PredictionRequest } from "../modules/common";

import type {
  DesignWorkflowExecution,
  DesignWorkflowExecutionStickiedSelection,
  DesignWorkflowExecutionSummary,
  IExecutionMetrics,
} from "./design-workflow-execution";
import { transformFilterGroupsData } from "./filter-groups/transform";
import type { FilterGroup, FilterGroups } from "./filter-groups/types";
import { FILTER_COLORS } from "./filter-groups/types";

interface ArchiveRestoreRequest {
  branchRootId?: string;
  executionId: string;
}

interface UpdateExecutionStickiedRowsRequest {
  id: string;
  data: DesignWorkflowExecutionStickiedSelection;
}

/**
 * The API persists colors as strings, but we originally stored the main hex
 * values. These original colors allow us to transform between those main hex
 * values and palette color names on read/write, remaining compatible with the
 * existing API and persisted data.
 */
const ORIGINAL_COLORS = [
  "#AFD4FA",
  "#D1A7AA",
  "#8B6F9E",
  "#DBDA84",
  "#A1E6AF",
  "#689494",
] as const;

export class DesignWorkflowExecutionClient extends ClientBase<DesignWorkflowExecution> {
  constructor(
    protected readonly projectId: string,
    protected readonly workflowId: string,
  ) {
    super({
      endpoint: `/api/v1/projects/${projectId}/design-workflows/${workflowId}/executions`,
      findField: "response",
      defaultQuery: {
        page: 1,
        per_page: 20,
      },
    });
  }

  static getInstance(projectId: string, workflowId: string) {
    return new this(projectId, workflowId);
  }

  public getExecutionMetrics(
    executionId: string,
    x: string,
    y: string,
  ): Promise<{ mjpd: IExecutionMetrics[]; spd: IExecutionMetrics[] }> {
    return this.request({
      url: `${this.endpoint}/${executionId}/metrics?x=${x}&y=${y}`,
    });
  }

  public useGetExecutionMetrics(
    executionId: string,
    x: string | undefined,
    y: string | undefined,
  ) {
    return useQuery({
      queryKey: [...this.getModelCacheKey(executionId), "metrics", { x, y }],
      queryFn: () => this.getExecutionMetrics(executionId, x!, y!),
      enabled: Boolean(x && y && executionId),
      staleTime: Infinity,
    });
  }

  public getExecutionSummary(
    executionId: string,
  ): Promise<DesignWorkflowExecutionSummary> {
    return this.request({
      url: `${this.endpoint}/${executionId}/summary`,
    });
  }

  public useGetExecutionSummary(
    executionId?: string,
    options?: Omit<
      UseQueryOptions<DesignWorkflowExecutionSummary>,
      "queryKey" | "queryFn"
    >,
  ): UseQueryResult<DesignWorkflowExecutionSummary> {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(executionId), "summary"],
      queryFn: () => this.getExecutionSummary(executionId ?? ""),
      staleTime: Infinity,
      enabled: Boolean(
        executionId && (options?.enabled ?? true) && this.isEnabled(),
      ),
    });
  }

  public makePrediction(executionId: string, request: PredictionRequest) {
    return this.request<ExecutionCandidate>({
      method: "POST",
      data: request,
      url: `${this.endpoint}/${executionId}/predict`,
    });
  }

  public useMakePredictionRequest(
    options?: Exclude<
      UseMutationOptions<
        ExecutionCandidate,
        AxiosError,
        { request: PredictionRequest; executionId: string }
      >,
      "mutationFn"
    >,
  ): UseMutationResult<
    ExecutionCandidate,
    AxiosError,
    { request: PredictionRequest; executionId: string }
  > {
    return useMutation({
      ...options,
      mutationFn: ({ request, executionId }) =>
        this.makePrediction(executionId, request),
    });
  }

  async archive({ branchRootId, executionId }: ArchiveRestoreRequest) {
    const response = await this.request({
      method: "PUT",
      url: `${this.endpoint}/${executionId}/archive`,
    });
    this.updateCacheAfterArchiveOrRestore(response, branchRootId);
    return response;
  }

  useArchive(
    options: Omit<
      UseMutationResult<ExecutionCandidate, AxiosError, ArchiveRestoreRequest>,
      "mutationFn"
    >,
  ) {
    return useMutation({ ...options, mutationFn: this.archive });
  }

  async restore({ branchRootId, executionId }: ArchiveRestoreRequest) {
    const response = await this.request({
      method: "PUT",
      url: `${this.endpoint}/${executionId}/restore`,
    });
    this.updateCacheAfterArchiveOrRestore(response, branchRootId);
    return response;
  }

  useRestore(
    options: Omit<
      UseMutationResult<ExecutionCandidate, AxiosError, ArchiveRestoreRequest>,
      "mutationFn"
    >,
  ) {
    return useMutation({ ...options, mutationFn: this.restore });
  }

  async getFilterGroups(executionId: string) {
    try {
      const result = await this.request<FilterGroups>({
        method: "GET",
        url: `${this.endpoint}/${executionId}/filter-groups`,
      });

      // respect valid persisted colors
      const matchedColors = result.filter_groups.map((filterGroup) => {
        if (FILTER_COLORS.includes(filterGroup.color)) {
          return filterGroup.color;
        } else {
          const originalIndex = ORIGINAL_COLORS.indexOf(filterGroup.color);
          if (originalIndex >= 0) {
            return FILTER_COLORS[originalIndex];
          }
        }
      });

      const usedColors = new Set(matchedColors);

      result.filter_groups = result.filter_groups.map((filterGroup, index) => {
        const matchedColor = matchedColors[index];
        // if any filter_groups had invalid color selections persisted, assign an available valid color
        const color =
          matchedColor ??
          FILTER_COLORS.find((filterColor) => !usedColors.has(filterColor)) ??
          FILTER_COLORS[0]; // this fallback should never match, but just-in-case
        usedColors.add(color);

        return {
          ...filterGroup,
          color,
        };
      });

      return result;
    } catch (e) {
      const error = e as AxiosError;
      if (error.response?.status === 404) {
        // Do not fail if no filter groups are found
        return { filter_groups: [] };
      }
      throw error;
    }
  }

  useGetFilterGroups(
    executionId: string,
    options?: Omit<
      UseQueryOptions<FilterGroups, AxiosError>,
      "queryKey" | "queryFn"
    >,
  ) {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(executionId), "filter-groups"],
      queryFn: () => this.getFilterGroups(executionId),
      select: (data) => transformFilterGroupsData(data),
    });
  }

  async updateFilterGroups({
    executionId,
    data,
  }: {
    executionId: string;
    data: FilterGroup[];
  }) {
    const result = await this.request<FilterGroups>({
      method: "PUT",
      url: `${this.endpoint}/${executionId}/filter-groups`,
      data: {
        filter_groups: data.map((filterGroup) => ({
          ...filterGroup,
          color: ORIGINAL_COLORS.includes(filterGroup.color)
            ? filterGroup.color
            : ORIGINAL_COLORS[FILTER_COLORS.indexOf(filterGroup.color)],
          newFilterGroup: undefined,
        })),
      },
    });
    this.invalidateAfterMutation(executionId);
    return result;
  }

  useUpdateFilterGroups(
    options?: Omit<
      UseMutationOptions<
        FilterGroups,
        AxiosError,
        { executionId: string; data: FilterGroup[] }
      >,
      "mutationFn"
    >,
  ) {
    return useMutation({
      ...options,
      mutationFn: (data) => this.updateFilterGroups(data),
    });
  }

  async getStickiedRows(executionId: string) {
    try {
      const result =
        await this.request<DesignWorkflowExecutionStickiedSelection>({
          method: "GET",
          url: `${this.endpoint}/${executionId}/stickied-rows`,
        });
      return result;
    } catch (e) {
      const error = e as AxiosError;
      if (error.response?.status === 404) {
        // Do not throw an error if no stickied rows are found
        return { descriptors: [], ingredients: {} };
      }
      throw error;
    }
  }

  useGetStickiedRows(
    executionId: string,
    options?: Omit<
      UseQueryOptions<DesignWorkflowExecutionStickiedSelection, AxiosError>,
      "queryKey" | "queryFn"
    >,
  ) {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(executionId), "stickied-rows"],
      queryFn: () => this.getStickiedRows(executionId),
    });
  }

  async updateStickiedRows({ id, data }: UpdateExecutionStickiedRowsRequest) {
    const result = await this.request<DesignWorkflowExecutionStickiedSelection>(
      {
        method: "PUT",
        url: `${this.endpoint}/${id}/stickied-rows`,
        data,
      },
    );
    this.invalidateAfterMutation(id);
    return result;
  }

  useUpdateStickiedRows(
    options?: Omit<
      UseMutationOptions<
        DesignWorkflowExecutionStickiedSelection,
        AxiosError,
        UpdateExecutionStickiedRowsRequest
      >,
      "mutationFn"
    >,
  ) {
    return useMutation({
      ...options,
      mutationFn: (data) => this.updateStickiedRows(data),
    });
  }

  /**
   * createConfig method is used to customize resource mutations
   *
   * This is used by this.create() to get the exact URL for creating a single model
   *
   * For a design workflow execution, the "score" is the portion sent to the
   * server to create an execution
   */
  protected createConfig(
    execution: Partial<DesignWorkflowExecution>,
  ): AxiosRequestConfig {
    return {
      method: "POST",
      url: this.endpoint,
      data: { score: execution.score },
    };
  }

  protected getExecutionCandidatesUrl(executionId: string): string {
    return `${this.endpoint}/${executionId}/candidates`;
  }

  protected toModel(payload: DesignWorkflowExecution): DesignWorkflowExecution {
    const model = super.toModel(payload);
    model.score.constraints.forEach(transformConstraint);
    return model;
  }

  private updateCacheAfterArchiveOrRestore(
    updatedExecution: DesignWorkflowExecution,
    branchRootId?: string,
  ) {
    this.invalidateAfterMutation(updatedExecution.id);
    this.addModelToCache(updatedExecution);

    if (branchRootId) {
      BranchRootClient.getInstance(this.projectId).invalidateAfterMutation(
        branchRootId,
      );
    }
  }
}
